import { HelpCircle } from 'lucide-react';
import type { SbCategory } from '@/components/builder/sb/CategoryChips';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';

// Matches GET /api/sb/questions response item. Source:
// src/sb-questions/dto/sb-question-response.dto.ts.
export interface SbQuestion {
  id: string;
  category: SbCategory;
  question: string;
}

interface QuestionCardsProps {
  questions: SbQuestion[];
  selectedQuestionId: string | null;
  onSelect: (question: SbQuestion) => void;
  isLoading?: boolean;
}

export function QuestionCards({
  questions,
  selectedQuestionId,
  onSelect,
  isLoading,
}: QuestionCardsProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="builder-field h-16 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (questions.length === 0) {
    return <p className="text-sm text-dream-navy/70">{STRINGS.builder.sb.questionsEmpty}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {questions.map((q) => {
        const isActive = q.id === selectedQuestionId;
        return (
          <button
            key={q.id}
            type="button"
            aria-pressed={isActive}
            onClick={() => onSelect(q)}
            className={cn(
              'flex items-start gap-3 rounded-lg p-4 text-left transition-colors',
              isActive ? 'border-[1.5px] border-dream-gold bg-dream-gold/20' : 'builder-field',
            )}
          >
            <HelpCircle aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-dream-navy" />
            <span className="flex-1 text-sm leading-relaxed text-dream-navy">{q.question}</span>
          </button>
        );
      })}
    </div>
  );
}
